import { FC, useEffect, useRef } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme, useMediaQuery, Box } from '@mui/material';
import { words, sample } from 'lodash';

import { Events } from '~/constants/events';
import { trackEvent } from '~/helpers/analytics';
import useLocalStorage from '~/hooks/useLocalStorage';
import Config from '~/lib/config';

const { Typography, AudioPlayer } = ToucanComponents;

interface TranslationOutputProps {
  sourcePhrase?: string;
}

const dogSounds = ['woof', 'bark', 'ruff', 'grrr', 'arf', 'barkbark'];

interface Map {
  [key: string]: {
    word: string;
    filename: string;
  };
}

const doggoDictionary: Map = {
  fluffy: {
    word: 'floofy',
    filename: 'fluffy',
  },
  human: {
    word: 'hooman',
    filename: 'human',
  },
  dog: {
    word: 'doggo',
    filename: 'dog',
  },
  tricked: {
    word: 'did a bamboozle',
    filename: 'tricked',
  },
  friend: {
    word: 'fren',
    filename: 'friend',
  },
  puppy: {
    word: 'pupperino',
    filename: 'puppy',
  },
  snack: {
    word: 'shmacko',
    filename: 'snack',
  },
  snacks: {
    word: 'shmackos',
    filename: 'snacks',
  },
  walk: {
    word: 'walkies',
    filename: 'walk',
  },
  treats: {
    word: 'treatos',
    filename: 'treats',
  },
  treat: {
    word: 'treato',
    filename: 'treat',
  },
  vet: {
    word: 'HECKIN BIG SCARE',
    filename: 'vet',
  },
  squirrel: {
    word: 'SQUIRREL',
    filename: 'squirrel',
  },
  veterinarian: {
    word: 'HECKIN BIG SCARE',
    filename: 'vet',
  },
  barkbox: {
    word: 'best boxerino',
    filename: 'barkbox',
  },
  park: {
    word: 'PARK',
    filename: 'park',
  },
  outside: {
    word: 'OUSSIDE',
    filename: 'outside',
  },
};

interface TranslationInformation {
  translation: string;
  filenames: string[];
}

export const TranslationOutput: FC<TranslationOutputProps> = ({ sourcePhrase }) => {
  const theme = useTheme();
  const isViewportAboveSm = useMediaQuery(theme.breakpoints.up('sm'));
  const [translatedBarkPhrases, setTranslatedBarkPhrases] = useLocalStorage('translatedBarkPhrases', {});
  let doggoTranslation;
  // Use ref since audioFileUrls is used in callback
  const audioFileUrls = useRef<string[]>([]);

  const getDoggoTranslationInformation = (text: string | undefined): TranslationInformation => {
    const translationInformationByWord = words(text, /[\w]+/g).map((word: string) => {
      if (doggoDictionary[word]) {
        return {
          word: doggoDictionary[word].word,
          filename: `${Config.SITE}/audio/bark/doggo_${doggoDictionary[word].filename}.mp3`,
        };
      }

      const dogSound = sample(dogSounds);
      return {
        word: dogSound,
        filename: `${Config.SITE}/audio/bark/doggo_${dogSound}.mp3`,
      };
    });

    interface TranslationInformationByWord {
      word: string | undefined;
      filename: string;
    }

    return {
      translation: translationInformationByWord.map((info: TranslationInformationByWord) => info.word).join(' '),
      filenames: translationInformationByWord.map((info: TranslationInformationByWord) => info.filename),
    };
  };

  const playDoggoTranslation = async () => {
    const audioInterface = {
      play: () => {
        let index = 0;

        const playRecursively = () => {
          if (index === audioFileUrls.current.length) {
            audioInterface.onended();
          }

          const generatedAudioFile = new Audio(audioFileUrls.current[index]);
          generatedAudioFile.addEventListener('ended', playRecursively);
          generatedAudioFile.play();
          index += 1;
        };

        playRecursively();
      },
      onended: () => {},
    };

    return audioInterface;
  };

  useEffect(() => {
    if (sourcePhrase) {
      trackEvent({ name: Events.BarkTranslationGenerated });
    }
  }, [sourcePhrase]);

  if (sourcePhrase) {
    const lowercasedSourcePhrase = sourcePhrase.toLowerCase();
    const storedTranslationInformation = translatedBarkPhrases[lowercasedSourcePhrase];
    const translationInformation =
      storedTranslationInformation || getDoggoTranslationInformation(lowercasedSourcePhrase);

    if (!storedTranslationInformation) {
      setTranslatedBarkPhrases({
        ...translatedBarkPhrases,
        [lowercasedSourcePhrase]: translationInformation,
      });
    }

    doggoTranslation = translationInformation.translation;
    audioFileUrls.current = translationInformation.filenames;
  }

  return (
    <Box
      css={css`
        background-color: ${ToucanColors.blue[100]};
        padding: ${theme.spacing(5)} ${theme.spacing(2)};
        width: 100%;
        ${theme.breakpoints.up('sm')} {
          padding: ${theme.spacing(4)};
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          min-height: 200px;
        }
      `}
    >
      <Box display="flex" alignItems="flex-start">
        <AudioPlayer
          buttonSize={`${isViewportAboveSm ? 32 : 16}px`}
          onAudioPlayRequested={playDoggoTranslation}
          isDisabled={!doggoTranslation}
        />
        <Typography
          variant={isViewportAboveSm ? 'md' : 'sm'}
          css={css`
            margin-left: ${theme.spacing(2)};
            color: ${doggoTranslation ? ToucanColors.blue[400] : ToucanColors.blue[200]};
          `}
        >
          {doggoTranslation || '. . .'}
        </Typography>
      </Box>
    </Box>
  );
};
